import Service, { inject as service } from '@ember/service';
import { macroCondition, isDevelopingApp } from '@embroider/macros';

import type ApiService from 'api/services/api';
import type AbilitiesService from 'ember-can/services/abilities';

import type RolesLocationsService from 'manage-access/services/roles-locations';
import type ScopesService from 'manage-access/services/scopes';

import iamGetPrincipalMemberData from 'manage-access/helpers/iam-get-principal-member-data';
import iamGetPrincipalResourceName from 'manage-access/helpers/iam-get-principal-resource-name';
import iamFindResourceNamePart from 'manage-access/helpers/iam-find-resource-name-part';

import { ParseResourceName } from 'core/utils/resource-name';
import { TYPE_ORGANIZATION } from 'common/utils/cloud-resource-types';
import { REQUIRED_TEST_GROUP_PERMISSIONS } from '../utils/constants.ts';
import { HashicorpCloudResourcemanagerPolicyBindingMemberType } from '@clients/cloud-resource-manager';

import type {
  HashicorpCloudResourcemanagerPolicyBindingMember,
  HashicorpCloudResourcemanagerProject,
} from '@clients/cloud-resource-manager';
import type { HashicorpCloudIamPrincipal } from '@clients/cloud-iam';
import type {
  ParsedResourceName,
  PartRecord,
} from 'manage-access/types/resource-name';
import type { ScopeLocationLink } from '../types/scope-location-link.ts';

export default class RoleAssignmentsMemberDetailsService extends Service {
  @service declare readonly api: ApiService;
  @service declare readonly abilities: AbilitiesService;
  @service declare readonly rolesLocations: RolesLocationsService;
  @service declare readonly scopes: ScopesService;

  async fetch({
    currentScopeLink,
    project,
    principal,
  }: {
    currentScopeLink?: ScopeLocationLink;
    project?: HashicorpCloudResourcemanagerProject;
    principal?: HashicorpCloudIamPrincipal;
  }): Promise<{
    groupAllowedPermissions: Array<string>;
    member: HashicorpCloudResourcemanagerPolicyBindingMember | undefined;
    sourceProject: HashicorpCloudResourcemanagerProject | undefined;
  }> {
    let parsedResourceName: ParsedResourceName | undefined;
    let projectPart: PartRecord | undefined;
    let resourceName: string | undefined;
    let sourceProject: HashicorpCloudResourcemanagerProject | undefined;

    const member = principal ? iamGetPrincipalMemberData(principal) : {};

    let groupAllowedPermissions: Array<string> = [];

    if (
      member?.memberType ===
      HashicorpCloudResourcemanagerPolicyBindingMemberType.GROUP
    ) {
      try {
        const { allowedPermissions } =
          await this.api.resourceManager.authorization.authorizationServiceTestIamPermissions(
            {
              permissions: REQUIRED_TEST_GROUP_PERMISSIONS,
              resourceName: principal?.group?.resourceName,
            },
          );

        if (allowedPermissions?.length) {
          groupAllowedPermissions = allowedPermissions;
        }
      } catch (e) {
        if (macroCondition(isDevelopingApp())) {
          console.error(e);
        }
      }
    }

    if (
      currentScopeLink?.type !== TYPE_ORGANIZATION &&
      project?.id &&
      member?.memberType ===
        HashicorpCloudResourcemanagerPolicyBindingMemberType.SERVICEPRINCIPAL
    ) {
      if (principal) {
        resourceName = iamGetPrincipalResourceName(principal);
      }

      if (resourceName) {
        parsedResourceName = ParseResourceName(
          resourceName,
        ) as ParsedResourceName;
      }

      if (parsedResourceName) {
        projectPart = iamFindResourceNamePart(parsedResourceName, {
          part: 'project',
        });
      }

      if (projectPart?.id) {
        if (projectPart.id === project.id) {
          sourceProject = project;
        } else {
          try {
            ({ project: sourceProject } =
              await this.api.resourceManager.project.projectServiceGet(
                projectPart.id,
              ));
          } catch (e) {
            if (macroCondition(isDevelopingApp())) {
              console.error(e);
            }
          }
        }
      }
    }

    return {
      groupAllowedPermissions,
      member,
      sourceProject,
    };
  }
}
